import type { SidebarSection, UUID } from "./sync/types";
import { t } from "./i18n";

// The sections every sidebar has: starred channels, then the channels and
// DMs no other section holds, sorted by name.
export const STARRED = "starred";
export const CHANNELS = "channels";
export const DMS = "dms";

export const builtin = (id: string) => id === STARRED || id === CHANNELS || id === DMS;

export function defaultSections(): SidebarSection[] {
  return [STARRED, CHANNELS, DMS].map((id) => ({ id, collapsed: false, channel_ids: [] }));
}

// sectionName is what a section's header says.
export function sectionName(s: SidebarSection): string {
  return s.id === STARRED ? t("Starred") : s.id === CHANNELS ? t("Channels") : s.id === DMS ? t("Direct messages") : (s.name ?? "");
}

export interface Placed<C> {
  section: SidebarSection;
  items: C[];
}

// arrange puts each channel in its section: starred and filed ones in the
// order the person gave them, the rest under Channels or Direct messages
// by name, or with recent set, newest message first.
export function arrange<C>(
  sections: SidebarSection[],
  channels: C[],
  id: (c: C) => UUID,
  isDM: (c: C) => boolean,
  name: (c: C) => string,
  recent?: (c: C) => string | undefined,
): Placed<C>[] {
  const byID = new Map(channels.map((c) => [id(c), c]));
  const filed = new Set<UUID>();
  const out = sections.map((section) => {
    const items: C[] = [];
    for (const cid of section.channel_ids) {
      const c = byID.get(cid);
      if (c && !filed.has(cid)) {
        filed.add(cid);
        items.push(c);
      }
    }
    return { section, items };
  });
  const byName = (a: C, b: C) => name(a).localeCompare(name(b));
  const byRecent = (a: C, b: C) => (recent!(b) ?? "").localeCompare(recent!(a) ?? "") || byName(a, b);
  const rest = channels.filter((c) => !filed.has(id(c))).sort(recent ? byRecent : byName);
  for (const p of out) {
    if (p.section.id === CHANNELS) p.items = rest.filter((c) => !isDM(c));
    if (p.section.id === DMS) p.items = rest.filter(isDM);
  }
  return out;
}

// moveChannel files a channel in a section, before another channel there
// or at its end; with Channels or Direct messages as the section, it takes
// the channel out of every other.
export function moveChannel(sections: SidebarSection[], channel: UUID, to: string, before?: UUID): SidebarSection[] {
  return sections.map((s) => {
    const ids = s.channel_ids.filter((x) => x !== channel);
    if (s.id !== to || s.id === CHANNELS || s.id === DMS) return { ...s, channel_ids: ids };
    const at = before ? ids.indexOf(before) : -1;
    return { ...s, channel_ids: at < 0 ? [...ids, channel] : [...ids.slice(0, at), channel, ...ids.slice(at)] };
  });
}

// sectionOf is the section a channel is starred or filed in, if any.
export function sectionOf(sections: SidebarSection[], channel: UUID): string | undefined {
  return sections.find((s) => s.channel_ids.includes(channel))?.id;
}

// addSection adds a section of one's own above Channels, and says its id.
export function addSection(sections: SidebarSection[], name: string): [SidebarSection[], string] {
  const id = "s-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  const at = Math.max(0, sections.findIndex((s) => s.id === CHANNELS));
  return [[...sections.slice(0, at), { id, name, collapsed: false, channel_ids: [] }, ...sections.slice(at)], id];
}

// removeSection deletes a section of one's own; its channels go back to
// Channels and Direct messages.
export function removeSection(sections: SidebarSection[], id: string): SidebarSection[] {
  return builtin(id) ? sections : sections.filter((s) => s.id !== id);
}

// moveSection moves a section up or down one place.
export function moveSection(sections: SidebarSection[], id: string, by: -1 | 1): SidebarSection[] {
  const i = sections.findIndex((s) => s.id === id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= sections.length) return sections;
  const next = [...sections];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export function updateSection(sections: SidebarSection[], id: string, change: Partial<Pick<SidebarSection, "name" | "collapsed">>): SidebarSection[] {
  return sections.map((s) => (s.id === id ? { ...s, ...change } : s));
}
